import React, { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';
import { Checkbox, Input } from 'antd';

import fontService, { FontWeight, TextType } from 'services/FontService';
import { Colors } from 'context';

export interface IRepeatGroupWidget {
  /** Привязка задаёт форму подполей — методист их не выбирает. */
  binding: string;
  value: unknown;
  onChange: (value: Record<string, unknown>[]) => void;
}

/**
 * Повторяемый блок анкеты (ТЗ 0039): опыт, проекты, образование, языки.
 *
 * 🔴 Набор подполей задаётся ПРИВЯЗКОЙ, а не методистом. Дай ему произвольные
 * подполя — и запись в резюме перестанет понимать, какое из них «компания»;
 * привязка испарится, а вместе с ней весь смысл анкеты.
 */
const SHAPES: Record<string, { key: string; kind: 'text' | 'num' | 'bool' }[]> = {
  'profile.experience': [
    { key: 'company', kind: 'text' },
    { key: 'position', kind: 'text' },
    { key: 'description', kind: 'text' },
    { key: 'start_year', kind: 'num' },
    { key: 'start_month', kind: 'num' },
    { key: 'end_year', kind: 'num' },
    { key: 'end_month', kind: 'num' },
    { key: 'is_current', kind: 'bool' },
  ],
  'profile.projects': [
    { key: 'title', kind: 'text' },
    { key: 'description', kind: 'text' },
    { key: 'role', kind: 'text' },
    { key: 'repo_url', kind: 'text' },
    { key: 'demo_url', kind: 'text' },
  ],
  'profile.education': [
    { key: 'institution', kind: 'text' },
    { key: 'degree', kind: 'text' },
    { key: 'specialization', kind: 'text' },
    { key: 'start_year', kind: 'num' },
    { key: 'end_year', kind: 'num' },
  ],
  'profile.languages': [
    { key: 'language', kind: 'text' },
    { key: 'level', kind: 'text' },
  ],
};

export const RepeatGroupWidget = memo(({ binding, value, onChange }: IRepeatGroupWidget) => {
  const { t } = useTranslation();
  const shape = SHAPES[binding] ?? [];
  // useMemo, а не выражение: иначе массив новый на каждый рендер и все
  // useCallback ниже пересоздаются вместе с ним.
  const rows = useMemo<Record<string, unknown>[]>(
    () => (Array.isArray(value) ? (value as Record<string, unknown>[]) : []),
    [value],
  );

  const update = useCallback(
    (index: number, key: string, next: unknown) => {
      onChange(rows.map((row, position) => (position === index ? { ...row, [key]: next } : row)));
    },
    [rows, onChange],
  );

  if (!shape.length) {
    return <Hint>{t('careerCenter.exercise.groupNotConfigured')}</Hint>;
  }

  return (
    <>
      {rows.map((row, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Card key={`row-${index}`}>
          {shape.map((sub) => (
            <Line key={sub.key}>
              <Label>{t(`careerCenter.exercise.sub.${sub.key}`)}</Label>
              {sub.kind === 'bool' ? (
                <Checkbox
                  checked={Boolean(row[sub.key])}
                  onChange={(event) => update(index, sub.key, event.target.checked)}
                />
              ) : (
                <Input
                  type={sub.kind === 'num' ? 'number' : 'text'}
                  value={(row[sub.key] as string) ?? ''}
                  onChange={(event) =>
                    update(
                      index,
                      sub.key,
                      sub.kind === 'num' ? Number(event.target.value) || null : event.target.value,
                    )
                  }
                />
              )}
            </Line>
          ))}
          <Remove
            type="button"
            onClick={() => onChange(rows.filter((_row, position) => position !== index))}>
            {t('careerCenter.exercise.removeRow')}
          </Remove>
        </Card>
      ))}
      <AddButton type="button" onClick={() => onChange([...rows, {}])}>
        {t('careerCenter.exercise.addRow')}
      </AddButton>
    </>
  );
});

const Card = styled.div`
  padding: 14px;
  margin-bottom: 12px;
  border: 1px solid ${Colors.additional_gray};
  border-radius: 10px;
`;

const Line = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
`;

const Label = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
`;

const Remove = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.medium })}
`;

const AddButton = styled.button`
  border: 1px dashed ${Colors.additional_gray};
  background: transparent;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 8px;
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const Hint = styled.p`
  margin: 0;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
`;
